import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { invoiceKeys } from '@/hooks/useInvoices';
import { useToast } from '@/hooks/use-toast';
import type { Invoice } from '@jobos/shared';
import { workspaceKeys } from './useInvoiceWorkspace';

/**
 * Lifecycle actions issued from the workspace.
 *
 * These exist rather than reusing useMarkInvoiceSent / useUpdateInvoice
 * because those take a jobId at hook-construction time to key their
 * job-scoped cache and optimistic updates. The workspace's rows each belong to
 * a DIFFERENT job, so there is no single jobId to construct them with — and
 * widening them to take it per-mutation would rework the optimistic path the
 * job page depends on, for a caller that does not need optimism.
 *
 * The endpoints are the same. What differs is only which caches are dropped
 * afterwards.
 */
export function useWorkspaceInvoiceActions() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  /**
   * Everything the workspace shows is derived from the ledger, so any lifecycle
   * change moves rows AND counts: a settled invoice must leave Sent and
   * appear in Paid without a refresh, and the tab numbers must move with it.
   * The job caches go too, so the job page is correct when it is next opened.
   */
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: workspaceKeys.all });
    queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
    queryClient.invalidateQueries({ queryKey: ['jobs'] });
  };

  const onError = (error: Error) =>
    toast({ title: 'Error', description: error.message, variant: 'destructive' });

  const markSent = useMutation({
    mutationFn: (invoiceId: string) => apiClient.post<Invoice>(`/invoices/${invoiceId}/mark-sent`),
    onSuccess: () => {
      invalidate();
      toast({ title: 'Marked as sent' });
    },
    onError,
  });

  /**
   * The hand-entered sent date. Same PATCH the job tab uses; what differs is
   * the invalidation, which is the whole reason this hook exists.
   *
   * Dating a draft moves it from Drafts to Sent and clearing a date moves it
   * back, so the row changes stage and the tab counts change with it — the full
   * invalidate(), not a cache patch.
   */
  const setSentDate = useMutation({
    mutationFn: ({ invoiceId, date }: { invoiceId: string; date: string | null }) =>
      apiClient.patch<Invoice>(`/invoices/${invoiceId}`, { sentAt: date }),
    onSuccess: (_data, variables) => {
      invalidate();
      toast({ title: variables.date === null ? 'Sent date cleared' : 'Sent date updated' });
    },
    onError,
  });

  const voidInvoice = useMutation({
    mutationFn: (invoiceId: string) =>
      apiClient.patch<Invoice>(`/invoices/${invoiceId}`, { status: 'voided' }),
    onSuccess: () => {
      invalidate();
      toast({ title: 'Invoice voided' });
    },
    onError,
  });

  const deleteInvoice = useMutation({
    mutationFn: (invoiceId: string) => apiClient.delete(`/invoices/${invoiceId}`),
    onSuccess: () => {
      invalidate();
      toast({ title: 'Invoice deleted' });
    },
    onError,
  });

  return { markSent, setSentDate, voidInvoice, deleteInvoice, invalidate };
}
